import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import discgoLogo from 'figma:asset/70d8fe074a29674a16552ebcf7b0d662532aa1b0.png';

const PLAYER_WEB_URL = import.meta.env.VITE_PLAYER_WEB_URL || 'http://localhost:5000';

export default function BookPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { session, loading, roles } = useAuth();
  const [courseName, setCourseName] = useState<string | null>(null);
  const [courseError, setCourseError] = useState(false);

  // Fetch course name for display while we figure out auth state
  useEffect(() => {
    if (!courseId) return;
    supabase
      .from('courses')
      .select('name, status')
      .eq('id', courseId)
      .single()
      .then(({ data, error }) => {
        if (error || !data || data.status !== 'active') {
          setCourseError(true);
        } else {
          setCourseName(data.name);
        }
      });
  }, [courseId]);

  useEffect(() => {
    if (loading) return;
    if (!courseId) return;

    if (!session) {
      // Not logged in — send to login with redirect param so we come back here after auth
      navigate(`/login?redirect=/book/${courseId}`);
      return;
    }

    const hasPlayerRole = roles.includes('player');
    if (!hasPlayerRole) {
      // Authed but no player role — render the "player account required" message
      return;
    }

    // Authed as player — redirect to player-web with bookCourse param + session tokens
    const targetUrl = `${PLAYER_WEB_URL}?bookCourse=${courseId}`;
    const redirectUrl = `${targetUrl}#access_token=${session.access_token}&refresh_token=${session.refresh_token}&expires_at=${session.expires_at}&expires_in=${session.expires_in}&token_type=${session.token_type}`;
    window.location.href = redirectUrl;
  }, [loading, session, roles, courseId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground lowercase">loading...</p>
      </div>
    );
  }

  if (courseError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <img src={discgoLogo} alt="Discgo" className="h-12 w-auto mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-foreground mb-2 lowercase">course not found</h1>
          <p className="text-muted-foreground mb-6 lowercase">
            this booking link may be invalid or the course is no longer available.
          </p>
          <a href="/" className="text-primary hover:underline lowercase text-sm">
            go to discgo
          </a>
        </div>
      </div>
    );
  }

  if (session && !roles.includes('player')) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <img src={discgoLogo} alt="Discgo" className="h-12 w-auto mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-foreground mb-2 lowercase">player account required</h1>
          <p className="text-muted-foreground mb-6 lowercase">
            {courseName
              ? `to book a round at ${courseName}, you need a player account.`
              : 'you need a player account to book tee times.'}
          </p>
          <a
            href={`/login?tab=signup&redirect=/book/${courseId}`}
            className="inline-block px-6 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-all lowercase"
          >
            create player account
          </a>
        </div>
      </div>
    );
  }

  // Redirect in progress — show a brief loading state with course name
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <img src={discgoLogo} alt="Discgo" className="h-12 w-auto mx-auto mb-4" />
        <p className="text-muted-foreground lowercase">
          {courseName ? `taking you to book ${courseName}...` : 'loading...'}
        </p>
      </div>
    </div>
  );
}
